import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { RatingInput } from '../components/RatingInput';
import { TextArea } from '../components/TextArea';
interface SurveyState {
  q1: number | null;
  q1_explanation: string;
  q2: number | null;
  q2_explanation: string;
  q3: number | null;
  q3_explanation: string;
  q4: number | null;
  q4_explanation: string;
  q5_tips: string;
}
const WEBHOOK_URL = 'https://hook.eu2.make.com/jy9ggy7ppjjn4ebcxlmpucqkdi11ixb6';
export function Survey() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Lees de "id" query parameter uit de URL bij het laden van de pagina
  const [serviceId] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('id') ?? '';
  });
  const [state, setState] = useState<SurveyState>({
    q1: null,
    q1_explanation: '',
    q2: null,
    q2_explanation: '',
    q3: null,
    q3_explanation: '',
    q4: null,
    q4_explanation: '',
    q5_tips: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof SurveyState, string>>>({});
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation
    const newErrors: Partial<Record<keyof SurveyState, string>> = {};
    // Gebruik een expliciete null-check zodat 0 een geldige score is
    if (state.q1 === null) newErrors.q1 = 'Dit veld is verplicht.';
    if (state.q2 === null) newErrors.q2 = 'Dit veld is verplicht.';
    if (state.q3 === null) newErrors.q3 = 'Dit veld is verplicht.';
    if (state.q4 === null) newErrors.q4 = 'Dit veld is verplicht.';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to first error smoothly
      const firstErrorElement = document.querySelector('.text-red-500');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_id: serviceId,
          tevredenheid: state.q1,
          tevredenheid_toelichting: state.q1_explanation,
          monteur_score: state.q2,
          monteur_toelichting: state.q2_explanation,
          communicatie: state.q3,
          communicatie_toelichting: state.q3_explanation,
          aanbeveling: state.q4,
          aanbeveling_toelichting: state.q4_explanation,
          opmerkingen: state.q5_tips
        })
      });
      if (!response.ok) {
        throw new Error(`Webhook reageerde met status ${response.status}`);
      }
      setIsSubmitted(true);
    } catch (err) {
      setSubmitError('Er ging iets mis bij het verzenden. Probeer het opnieuw of neem contact met ons op.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="w-full max-w-3xl mx-auto" data-id="element-30" data-mp="335nyin9u3qdq">
      <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden relative" data-id="element-31" data-mp="333tcf5mtts82">
        {/* Top Progress / Header Bar */}
        <div className="px-6 pt-6 pb-2" data-id="element-32" data-mp="2bfj5y1bx7x1f">
          <div className="h-2 w-full bg-gray-100 rounded-full" data-id="element-33" data-mp="2bgkun8lv0i1m" />
        </div>

        <AnimatePresence mode="wait" data-id="element-34" data-mp="2fh4q87qsbdln">
          {!isSubmitted ? <motion.form key="form" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.3
        }} onSubmit={handleSubmit} className="p-6 sm:p-10 pt-4" data-id="element-35" data-mp="2kewij2vcd1qd">
              <h3 className="text-[#0b2a4a] text-xl font-bold mb-8 border-b border-gray-100 pb-4" data-id="element-36" data-mp="3udxuhy1qvmqz">
                Uw beoordeling
              </h3>

              <div className="space-y-10" data-id="element-37" data-mp="c0q27wwfqany">
                <div className="space-y-6 bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100" data-id="element-38" data-mp="786n2vi3n7h1">
                  <RatingInput label="1. Hoe tevreden ben je met de service van krnwtr+?" value={state.q1} onChange={val => {
                setState(s => ({
                  ...s,
                  q1: val
                }));
                if (errors.q1) setErrors(e => ({
                  ...e,
                  q1: undefined
                }));
              }} error={errors.q1} required data-id="element-39" data-mp="lx30l82xdcp1" />
                  <TextArea id="q1_explanation" label="Toelichting (optioneel)" placeholder="Vertel ons meer over uw ervaring..." value={state.q1_explanation} onChange={e => setState(s => ({
                ...s,
                q1_explanation: e.target.value
              }))} data-id="element-40" data-mp="2ijvnel9ioyoe" />
                </div>

                <div className="space-y-6 bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100" data-id="element-41" data-mp="23vhk6986v87b">
                  <RatingInput label="2. Hoe beoordeel je onze monteur?" value={state.q2} onChange={val => {
                setState(s => ({
                  ...s,
                  q2: val
                }));
                if (errors.q2) setErrors(e => ({
                  ...e,
                  q2: undefined
                }));
              }} error={errors.q2} required data-id="element-42" data-mp="1pw8191sllzzn" />
                  <TextArea id="q2_explanation" label="Toelichting (optioneel)" placeholder="Vertel ons meer over uw ervaring met de monteur..." value={state.q2_explanation} onChange={e => setState(s => ({
                ...s,
                q2_explanation: e.target.value
              }))} data-id="element-43" data-mp="3dsfnt49kndee" />
                </div>

                <div className="space-y-6 bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100" data-id="element-44" data-mp="3rryv5uuk6kpn">
                  <RatingInput label="3. Hoe was de communicatie met krnwtr+?" value={state.q3} onChange={val => {
                setState(s => ({
                  ...s,
                  q3: val
                }));
                if (errors.q3) setErrors(e => ({
                  ...e,
                  q3: undefined
                }));
              }} error={errors.q3} required data-id="element-45" data-mp="t41mvjw12fqj" />
                  <TextArea id="q3_explanation" label="Toelichting (optioneel)" placeholder="Vertel ons meer over de communicatie..." value={state.q3_explanation} onChange={e => setState(s => ({
                ...s,
                q3_explanation: e.target.value
              }))} data-id="element-46" data-mp="1el91nt0vn0iy" />
                </div>

                <div className="space-y-6 bg-gray-50/50 p-4 sm:p-6 rounded-xl border border-gray-100" data-id="element-47" data-mp="22cwi1h0iit6z">
                  <RatingInput label="4. Hoe waarschijnlijk is het dat je krnwtr+ zou aanbevelen aan vrienden of andere professionals in je vakgebied?" value={state.q4} minLabel="0 = zeer onwaarschijnlijk" maxLabel="10 = zeer waarschijnlijk" onChange={val => {
                setState(s => ({
                  ...s,
                  q4: val
                }));
                if (errors.q4) setErrors(e => ({
                  ...e,
                  q4: undefined
                }));
              }} error={errors.q4} required data-id="element-48" data-mp="2nlrzjx0hm42o" />
                  <TextArea id="q4_explanation" label="Waarom geef je dit cijfer? (optioneel)" placeholder="Vertel ons waarom je dit cijfer geeft..." value={state.q4_explanation} onChange={e => setState(s => ({
                ...s,
                q4_explanation: e.target.value
              }))} data-id="element-49" data-mp="1m52dg21rbweb" />
                </div>

                <div className="pt-4 border-t border-gray-100" data-id="element-50" data-mp="3nw1fa7usrimk">
                  <TextArea id="q5_tips" prominentLabel label="5. Heb je tot slot nog andere tips, opmerkingen of ideeën voor ons?" placeholder="Optionele opmerkingen of extra informatie..." value={state.q5_tips} onChange={e => setState(s => ({
                ...s,
                q5_tips: e.target.value
              }))} data-id="element-51" data-mp="ffg6ar260pjg" />
                </div>
              </div>

              <div className="mt-10 flex flex-col items-end gap-3" data-id="element-52" data-mp="2w1hcei2i39pb">
                {submitError && <p className="text-red-500 text-sm text-right" data-id="element-53" data-mp="1c45ehbacw9xm">
                    {submitError}
                  </p>}
                <button type="submit" disabled={isSubmitting} className="bg-gradient-to-r from-[#14b8a6] to-[#0b2a4a] text-white font-medium py-3 px-8 rounded-md hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#14b8a6] shadow-md disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-54" data-mp="ybp7jzec3rrf">
                  {isSubmitting ? 'Verzenden...' : 'Verzenden'}
                </button>
              </div>
            </motion.form> : <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="p-12 sm:p-20 flex flex-col items-center text-center min-h-[400px] justify-center" data-id="element-55" data-mp="ketw60am1o87">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            delay: 0.2,
            bounce: 0.5
          }} data-id="element-56" data-mp="2jxf3r6fnbksb">
                <CheckCircle2 className="w-20 h-20 text-[#14b8a6] mb-6" data-id="element-57" data-mp="37n9d3e0rar7t" />
              </motion.div>
              <h3 className="text-[#0b2a4a] text-2xl font-bold mb-3" data-id="element-58" data-mp="3gm4m0xv6qfet">
                Bedankt voor je feedback!
              </h3>
              <p className="text-gray-600 max-w-sm" data-id="element-59" data-mp="3ck9lhvl2w623">
                We waarderen de tijd die je hebt genomen om je ervaring met ons
                te delen. Hiermee kunnen we onze service blijven verbeteren.
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}